import DownloadOutlined from '@ant-design/icons/DownloadOutlined'
import FileTextOutlined from '@ant-design/icons/FileTextOutlined'
import LoadingOutlined from '@ant-design/icons/LoadingOutlined'
import SaveOutlined from '@ant-design/icons/SaveOutlined'
import { pdf } from '@react-pdf/renderer'
import { Alert, Button, message, Progress, Space, Typography } from 'antd'
import { format } from 'date-fns'
import { useRolodexEdit } from 'features/fde'
import { useContextBridge } from 'its-fine'
import { FfsReportSectionId } from 'pages/reporting/reports/ffs/ffsReportData'
import {
  FfsReportContext,
  useFfsReportOps,
} from 'pages/reporting/reports/ffs/FfsReportProvider'
import { PDFDocument } from 'pages/reporting/shared/pdfGeneration/PDFDocument'
import { PDFGenerationSection } from 'pages/reporting/shared/types'
import React, { useCallback, useRef, useState } from 'react'

const { Title, Text } = Typography

const RenderReact: React.FC = () => {
  const contextValue = useFfsReportOps()
  const {
    pdfState,
    publishLatestDraftPDF,
    handleDownloadLatestDraft,
    handleDownloadPublishedPdf,
    isSubmitting,
    report,
    metadataEntryId,
  } = contextValue

  // State for PDF generation
  const [isGenerating, setIsGenerating] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentStep, setCurrentStep] = useState<string>('')
  // Store section pages outside of PDF Document so we can render the TOC with correct page numbers
  const initialSectionPagesRef = useRef<Record<string, number>>({})

  // API hooks
  const { rolodexUploadFile } = useRolodexEdit()

  const Bridge = useContextBridge()

  const handleOnPageRender = useCallback(
    ({ id, pageNumber }: { id: string; pageNumber: number }) => {
      setProgress((prev) => Math.min(prev + 8, 90))
      setCurrentStep(`Generating ${id} page...`)
      initialSectionPagesRef.current = {
        ...initialSectionPagesRef.current,
        [id]: pageNumber,
      }
    },
    [setProgress, setCurrentStep]
  )

  // Generate PDF
  const generatePDF = useCallback(async () => {
    setProgress(0)
    setCurrentStep('Preparing PDF document...')
    setProgress(20)

    // First render populates page numbers, second render creates final PDF
    const initialPdfInstance = (
      <FfsReportContext.Provider value={contextValue}>
        <Bridge>
          <PDFDocument
            report={report}
            onPageRender={handleOnPageRender}
            initialSectionPages={initialSectionPagesRef.current}
          />
        </Bridge>
      </FfsReportContext.Provider>
    )

    setCurrentStep('Initializing PDF...')
    await pdf(initialPdfInstance).toBlob()
    setProgress(20)

    const pdfInstance = (
      <FfsReportContext.Provider value={contextValue}>
        <Bridge>
          <PDFDocument
            report={report}
            onPageRender={handleOnPageRender}
            initialSectionPages={initialSectionPagesRef.current}
          />
        </Bridge>
      </FfsReportContext.Provider>
    )

    setCurrentStep('Finalizing PDF...')
    const blob = await pdf(pdfInstance).toBlob()
    setProgress(100)
    return blob
  }, [contextValue, report, Bridge, handleOnPageRender])

  // Generate and save PDF draft
  const handleGeneratePDF = useCallback(async () => {
    try {
      setIsGenerating(true)
      const pdfBlob = await generatePDF()

      const timestamp = format(new Date(), 'yyyyMMdd-HHmmss')
      const path = `reportPDFDrafts/report-${metadataEntryId}-draft-${timestamp}.pdf`
      await rolodexUploadFile(metadataEntryId, pdfBlob, path)

      message.success('PDF generated successfully')
      // Reload the page or update state to show the new draft
      window.location.reload()
    } catch (error) {
      console.error('Failed to generate PDF:', error)
      message.error('Failed to generate PDF')
    } finally {
      setIsGenerating(false)
      setProgress(0)
      setCurrentStep('')
    }
  }, [generatePDF, metadataEntryId, rolodexUploadFile])

  return (
    <div style={{ padding: '24px', maxWidth: '800px' }}>
      <Title level={2}>Generate PDF</Title>
      <Text>
        Generate and download the final report PDF. You can create a draft
        version for review or publish the final version.
      </Text>

      <div style={{ marginTop: '32px' }}>
        <Space direction='vertical' size='large' style={{ width: '100%' }}>
          <div>
            <Title level={4}>Draft PDF</Title>
            <Text type='secondary'>
              Generate a draft PDF for review and internal use.
            </Text>
            <div style={{ marginTop: '16px' }}>
              <Space
                direction='vertical'
                size='small'
                style={{ width: '100%' }}>
                {isGenerating ? (
                  <div style={{ padding: '20px', textAlign: 'center' }}>
                    <Space
                      direction='vertical'
                      align='center'
                      style={{ width: '100%' }}>
                      <LoadingOutlined style={{ fontSize: 24 }} />
                      <Progress percent={progress} status='active' />
                      <Text>{currentStep}</Text>
                    </Space>
                  </div>
                ) : (
                  <Space wrap>
                    <Button
                      type='primary'
                      icon={<SaveOutlined />}
                      onClick={handleGeneratePDF}
                      loading={isGenerating}
                      disabled={isGenerating}>
                      Generate PDF
                    </Button>
                    {pdfState.latestDraftTimestamp && (
                      <>
                        <Button
                          icon={<FileTextOutlined />}
                          onClick={handleDownloadLatestDraft}
                          loading={isSubmitting}>
                          Download Latest Draft
                        </Button>
                        <Text type='secondary'>
                          Last generated:{' '}
                          {new Date(
                            pdfState.latestDraftTimestamp
                          ).toLocaleString()}
                        </Text>
                      </>
                    )}
                  </Space>
                )}
              </Space>
            </div>
          </div>

          <div>
            <Title level={4}>Published PDF</Title>
            <Text type='secondary'>
              Publish and download the final official report PDF.
            </Text>
            <div style={{ marginTop: '16px' }}>
              <Space direction='vertical' size='small'>
                {!pdfState.hasPublishedPdf ? (
                  <Button
                    type='primary'
                    danger
                    icon={<DownloadOutlined />}
                    onClick={publishLatestDraftPDF}
                    loading={isSubmitting}>
                    Publish Final PDF
                  </Button>
                ) : (
                  <Space>
                    <Button
                      type='primary'
                      icon={<DownloadOutlined />}
                      onClick={handleDownloadPublishedPdf}>
                      Download Published PDF
                    </Button>
                    <Button
                      type='default'
                      onClick={publishLatestDraftPDF}
                      loading={isSubmitting}>
                      Update Published PDF
                    </Button>
                  </Space>
                )}
                {pdfState.hasPublishedPdf && (
                  <Alert
                    message='PDF Published'
                    description='The official report PDF has been published and is available for download.'
                    type='success'
                    showIcon
                  />
                )}
              </Space>
            </div>
          </div>
        </Space>
      </div>
    </div>
  )
}

export const PDFGeneration: PDFGenerationSection = {
  RenderReact,
  required: false,
  id: FfsReportSectionId.PDF_GENERATION,
  title: 'Generate PDF',
  description: 'Generate and download final report',
}
